import { Box, Skeleton } from '@mui/material';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { GroupLabel, KitButton, KitPill } from '../common/MobileKit';
import MobileSheet from '../common/MobileSheet';
import { protoIcon } from '../common/ProtoIcon';
import {
    KitChip,
    KitChips,
    KitEmpty,
    KitEntityCard,
    KitFab,
    KitField,
    KitInput,
    KitSearch,
    KitSelect,
    KitStickyToolbar,
    KitToolbarButton,
} from '../common/MobileKitExtras';
import { useAppBarAction } from '../layout/AppBarActions';
import { BUCKET_ORDER, orderDeadline, orderStatusView } from './orderMobileUtils';

/**
 * Lista de ordens no shell mobile — `prototipos-mobile/ordens/lista.html`.
 * Só apresentação: filtros, paginação, permissões e navegação vêm de `pages/service_order/List.js`.
 */

const CalendarIcon = protoIcon('calendar');
const SlidersIcon = protoIcon('sliders');
const GaugeIcon = protoIcon('gauge');
const PlusIcon = protoIcon('plus');
const OrdersIcon = protoIcon('orders');

// Altura da barra superior "grande" das telas raiz (10 + 40 + título 28px/1.4 + 2 + 6 + 6) + safe-area.
const LARGE_BAR_OFFSET = 'calc(103px + env(safe-area-inset-top, 0px))';

// Chips de status do protótipo -> valor real da API (a API chama "Faturada" de "Paga").
const STATUS_CHIPS = [
    { value: '', label: 'Todas' },
    { value: 'Não Iniciada', label: 'Não iniciada' },
    { value: 'Em Execução', label: 'Em execução' },
    { value: 'Finalizada', label: 'Finalizada' },
    { value: 'Cancelada', label: 'Cancelada' },
    { value: 'Paga', label: 'Faturada' },
];

const toInput = (value) => (value && value.isValid() ? value.format('YYYY-MM-DD') : '');
const fromInput = (text) => {
    if (!text) return null;
    const parsed = dayjs(text, 'YYYY-MM-DD', true);
    return parsed.isValid() ? parsed : null;
};

function OrderCard({ order, onOpen }) {
    const view = orderStatusView(order.status);
    const deadline = orderDeadline(order);
    return (
        <KitEntityCard stripeColor={view.color} onClick={() => onOpen(order)}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <Box component="span" sx={{ fontWeight: 800, fontSize: 15, letterSpacing: '-.01em' }}>OS #{order.number}</Box>
                <KitPill tone={view.tone}>{view.label}</KitPill>
            </Box>
            <Box sx={{ m: '8px 0 10px' }}>
                <Box sx={{ fontWeight: 600, fontSize: 14, lineHeight: 1.4, color: 'text.primary' }}>{order.customer_name || '—'}</Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', fontSize: 12, color: 'text.disabled', lineHeight: 1.4 }}>
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    <CalendarIcon size={13} />{deadline.label}
                </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px dashed', borderColor: 'divider', mt: '12px', pt: '10px' }}>
                <Box component="span" sx={{ fontWeight: 800, fontSize: 16, letterSpacing: '-.02em' }}>R$ {order.amount ?? '0,00'}</Box>
                <Box component="span" sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.4 }}>{deadline.foot}</Box>
            </Box>
        </KitEntityCard>
    );
}

export default function MobileOrdersList({
    orders,
    loading,
    totalCount,
    hasMore,
    onLoadMore,
    status,
    onStatusChange,
    search,
    onSearchChange,
    customers,
    customer,
    onCustomerChange,
    appliedPeriod,
    onApplyFilters,
    onClearFilters,
    canCreate,
    onNew,
    canUpcoming,
    onUpcoming,
    onOpen,
}) {
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [draftCustomer, setDraftCustomer] = useState(customer);
    const [draftStart, setDraftStart] = useState(appliedPeriod.start);
    const [draftEnd, setDraftEnd] = useState(appliedPeriod.end);

    const openFilters = () => {
        setDraftCustomer(customer);
        setDraftStart(appliedPeriod.start);
        setDraftEnd(appliedPeriod.end);
        setFiltersOpen(true);
    };

    useAppBarAction({
        actions: [
            { label: 'Filtros', icon: SlidersIcon, onClick: openFilters },
            ...(canUpcoming ? [{ label: 'Próximos serviços', icon: GaugeIcon, onClick: onUpcoming }] : []),
        ],
    });

    const groups = useMemo(() => {
        const byBucket = {};
        orders.forEach((order) => {
            const { bucket } = orderDeadline(order);
            (byBucket[bucket] = byBucket[bucket] || []).push(order);
        });
        return BUCKET_ORDER.filter((b) => byBucket[b.key]).map((b) => ({ ...b, items: byBucket[b.key] }));
    }, [orders]);

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }}>
            <KitStickyToolbar top={LARGE_BAR_OFFSET}>
                <KitSearch
                    value={search}
                    onChange={(e) => onSearchChange(e.target.value)}
                    placeholder="Número, cliente, modelo ou placa…"
                    inputProps={{ 'aria-label': 'Buscar ordem', inputMode: 'search', enterKeyHint: 'search' }}
                />
                <KitToolbarButton aria-label="Filtros" onClick={openFilters}>
                    <SlidersIcon size={20} />
                </KitToolbarButton>
            </KitStickyToolbar>

            <KitChips>
                {STATUS_CHIPS.map((chip) => (
                    <KitChip
                        key={chip.value || 'all'}
                        active={status === chip.value}
                        count={status === chip.value && !loading ? totalCount : undefined}
                        onClick={() => onStatusChange(chip.value)}
                    >
                        {chip.label}
                    </KitChip>
                ))}
            </KitChips>

            <Box sx={{ mt: '12px' }} />

            {loading && orders.length === 0 && (
                <Box sx={{ mt: '18px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {[1, 2, 3].map((n) => <Skeleton key={n} variant="rounded" height={132} sx={{ borderRadius: '16px' }} />)}
                </Box>
            )}

            {!loading && orders.length === 0 && (
                <KitEmpty icon={OrdersIcon} title="Nenhuma ordem encontrada">
                    Ajuste a busca ou os filtros para ver outras ordens de serviço.
                </KitEmpty>
            )}

            {groups.map((group) => (
                <Box key={group.key}>
                    <GroupLabel>{group.key === 'late' ? `${group.label} · ${group.items.length}` : group.label}</GroupLabel>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {group.items.map((order) => (
                            <OrderCard key={order.order_service_id} order={order} onOpen={onOpen} />
                        ))}
                    </Box>
                </Box>
            ))}

            {hasMore && (
                <KitButton block sx={{ mt: '14px' }} onClick={onLoadMore} disabled={loading}>
                    {loading ? 'Carregando…' : 'Carregar mais'}
                </KitButton>
            )}

            {canCreate && <KitFab aboveTabbar label="Nova OS" icon={PlusIcon} onClick={onNew} />}

            <MobileSheet
                open={filtersOpen}
                onClose={() => setFiltersOpen(false)}
                title="Filtros"
                footer={(
                    <>
                        <KitButton
                            sx={{ flex: 1 }}
                            onClick={() => {
                                setFiltersOpen(false);
                                onClearFilters();
                            }}
                        >
                            Limpar
                        </KitButton>
                        <KitButton
                            variant="primary"
                            sx={{ flex: 1 }}
                            onClick={() => {
                                setFiltersOpen(false);
                                onApplyFilters({ customer: draftCustomer, start: draftStart, end: draftEnd });
                            }}
                        >
                            Aplicar
                        </KitButton>
                    </>
                )}
            >
                <KitField label="Cliente">
                    <KitSelect value={draftCustomer} onChange={(e) => setDraftCustomer(e.target.value)} aria-label="Cliente">
                        <option value="">Todos</option>
                        {customers.map((c) => (
                            <option key={c.ID} value={c.ID}>{c.Name}</option>
                        ))}
                    </KitSelect>
                </KitField>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '12px' }}>
                    <KitField label="De">
                        <KitInput type="date" aria-label="Data inicial" value={toInput(draftStart)} onChange={(e) => setDraftStart(fromInput(e.target.value))} />
                    </KitField>
                    <KitField label="Até">
                        <KitInput type="date" aria-label="Data final" value={toInput(draftEnd)} onChange={(e) => setDraftEnd(fromInput(e.target.value))} />
                    </KitField>
                </Box>
            </MobileSheet>
        </Box>
    );
}
